/* eslint-disable camelcase */

import React, { createElement } from 'react'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'

// Local import
import TextField from './BasicFields/TextField'
import NumberField from './BasicFields/NumberField'
import Grade from './QuestionFields/Grade'
import Selection from './QuestionFields/Selection'
import Photos from './QuestionFields/Photos'
import Comment from './QuestionFields/Comment'
import Numeric from './QuestionFields/Numeric'
import Signature from './QuestionFields/Signature'
import Variable from './QuestionFields/Variable'
import DateTime from './QuestionFields/DateTime'
import ContentVariable from './ContentFields/ContentVariable'
import Title from './ContentFields/Title'
import Picture from './ContentFields/Picture'
import Description from './ContentFields/Description'
import Pdf from './ContentFields/Pdf'
import { idGenerator, isNewRecord } from '../../utils/idHelpers'
import { dispatchConfirmDelete } from '../shared/ConfirmModal'
import QuestionConditionsBanner from './QuestionConditionsBanner'
import AddConditionButton from './AddConditionButton'
import { linkedRuleNumbers, SOURCE_CONTEXTS } from './ConditionsDrawer/helpers'
import PlusIcon from '../ui/icons/PlusIcon'
import TrashCanIcon from '../ui/icons/TrashCanIcon'
import DuplicateIcon from '../ui/icons/DuplicateIcon'
import Button from '../ui/Button'
import Chip from '../ui/Chip'

const SELECTION_CONTEXTS = ['selection', 'selection_multi']

const SECTION_CARD = 'nested-fields nested-section tw:mb-2!'
const SECTION_HEADER_WRAP = 'tw:sticky tw:top-[var(--section-sticky-top)] tw:z-20 tw:bg-white! '
  + 'tw:-mt-2! tw:pt-2! '
  + 'tw:not-has-[+_.inner-nested-section.show]:[&>.header-nested-section]:rounded-b-lg!'
const SECTION_HEADER = 'header-nested-section c-pointer tw:flex! tw:items-center! tw:gap-2! '
  + 'tw:bg-gray-100! tw:px-[19px]! tw:py-0! tw:min-h-[63px]! '
  + 'tw:border! tw:border-solid! tw:border-gray-300! tw:rounded-t-lg!'
const SECTION_TITLE = 'tw:min-w-0 tw:text-[14px] tw:font-light tw:text-gray-700 tw:break-words'
const SECTION_NAME = 'tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:flex-1 tw:min-w-0'

const pluralize = (count, singular) => `${count} ${singular}${count > 1 ? 's' : ''}`
const SECTION_BODY = 'inner-nested-section tw:bg-white! tw:px-6! tw:py-5! '
  + 'tw:border! tw:border-t-0! tw:border-solid! tw:border-gray-300! tw:rounded-b-lg!'
const DRAG_HANDLE = 'fas fa-arrows-alt tw:text-gray-500 tw:cursor-move tw:shrink-0'
const DUPLICATE_ICON = 'tw:text-gray-500 tw:cursor-pointer tw:hover:text-gray-700 tw:shrink-0'
const TRASH_ICON = 'tw:text-gray-500 tw:cursor-pointer tw:hover:text-gray-700 tw:shrink-0'

const updateErrors = (stateErrors, getErrors) => {
  const parentErrors = getErrors()
  const currentErrors = stateErrors || {}

  if (currentErrors.isNew)
    delete currentErrors.isNew

  const hasParentErrors = parentErrors && Object.keys(parentErrors).length > 0
  const hasCurrentErrors = Object.keys(currentErrors).length > 0

  if (!hasParentErrors)
    return hasCurrentErrors ? { isNew: true } : {}

  if (JSON.stringify(parentErrors) !== JSON.stringify(currentErrors))
    return { ...parentErrors, isNew: true }

  return currentErrors
}


class SectionField extends React.Component {
  static collapseList(elementId) {
    window.$(`#${elementId}`).collapse('hide')
  }

  static toggleAddPanel(openId, closeId) {
    const other = window.$(`#${closeId}`)
    if (!other.hasClass('show')) {
      window.$(`#${openId}`).collapse('toggle')
      return
    }
    other.removeClass('show')
    window.$(`#${openId}`).addClass('show')
  }

  static duplicateQuestionWithPictureDuplicate = async questionToDuplicate => {
    if (questionToDuplicate.context !== 'picture')
      return

    // For dynamic content (using variable), preserve variable_id and don't duplicate file
    if (questionToDuplicate.variable_id) {
      // Keep the variable_id, which will make isDynamic = true in the component
      return
    }

    // For static content, duplicate the file
    if (questionToDuplicate.picture && questionToDuplicate.picture.url) {
      const response = await fetch(questionToDuplicate.picture.url)
      const blob = await response.blob()
      const image = new File([blob], questionToDuplicate.picture.filename, { type: blob.type })
      questionToDuplicate.picture = image
      questionToDuplicate.picture.url = URL.createObjectURL(image)
      questionToDuplicate.picture.filename = image.name
      questionToDuplicate.changed = true
    }
  }

  static duplicateQuestionWithPdfDuplicate = async questionToDuplicate => {
    if (questionToDuplicate.context !== 'pdf')
      return

    // For dynamic content (using variable), preserve variable_id and don't duplicate file
    if (questionToDuplicate.variable_id) {
      // Keep the variable_id, which will make isDynamic = true in the component
      return
    }

    // For static content, duplicate the file
    if (questionToDuplicate.pdf && questionToDuplicate.pdf.url) {
      const response = await fetch(questionToDuplicate.pdf.url)
      const blob = await response.blob()
      const pdfFile = new File([blob], questionToDuplicate.pdf.filename, { type: blob.type })
      questionToDuplicate.pdf = pdfFile
      questionToDuplicate.pdf.url = URL.createObjectURL(pdfFile)
      questionToDuplicate.pdf.filename = pdfFile.name
      questionToDuplicate.changed = true
    }
  }

  static duplicateQuestionWithOptionsDuplicate = questionToDuplicate => {
    if (typeof questionToDuplicate.options === 'undefined' || questionToDuplicate.options.length === 0)
      return

    questionToDuplicate.options = questionToDuplicate.options.map(option => ({
      ...option,
      id: idGenerator()
    }))
  }

  static duplicateQuestion = async(question, { prefixTitle = true } = {}) => {
    const title = prefixTitle && question.title ? `(Copie) ${question.title}` : question.title
    const questionToDuplicate = { ...question, id: idGenerator(), title }
    SectionField.duplicateQuestionWithOptionsDuplicate(questionToDuplicate)
    await SectionField.duplicateQuestionWithPictureDuplicate(questionToDuplicate)
    await SectionField.duplicateQuestionWithPdfDuplicate(questionToDuplicate)
    return questionToDuplicate
  }

  constructor(props) {
    super(props)

    this.state = {
      id: props.section.id,
      name: props.section.name,
      position: props.section.position,
      coefficient: props.section.coefficient,
      has_disabled_status: props.section.has_disabled_status,
      questions: props.section.questions,
      removed_question_ids: [],
      errors: props.section.errors || {}
    }

    this.fieldName = props.fieldName
    this.collectFrequency = props.collectFrequency
  }

  static getDerivedStateFromProps(props, state) {
    return { errors: updateErrors(state.errors, props.getErrors) }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.state.name !== nextState.name ||
      this.state.coefficient !== nextState.coefficient ||
      this.state.position !== nextState.position ||
      this.state.questions.length !== nextState.questions.length ||
      this.state.has_disabled_status !== nextState.has_disabled_status ||
      nextState.errors.isNew === true ||
      this.props.rules !== nextProps.rules
    )
  }

  componentDidUpdate() {
    this.props.handleSectionChange(this.state)
  }

  handleTrashClick = event => {
    event.stopPropagation()
    if (isNewRecord(this.state.id)) {
      this.props.handleRemoveSection(event)
      return
    }
    const count = this.state.questions.length
    let message = `La section "${this.state.name}" sera supprimée.`
    if (count > 0) {
      const plural = count > 1 ? 's' : ''
      message = `Cette section contient ${count} question${plural}.`
        + ' Tous les résultats associés seront définitivement perdus.'
    }
    const onConfirm = () => this.props.handleRemoveSection({ stopPropagation: Function.prototype })
    dispatchConfirmDelete('Supprimer la section', message, onConfirm)
  }

  handleNameChange = event => this.setState({ name: event.target.value })

  handleCoefficientChange = event => this.setState({ coefficient: event.target.value })

  handleDisabledStatusChange = event => this.setState({ has_disabled_status: event.target.checked })

  handleOnDragEnd = result => {
    const { destination, source } = result

    if (!destination)
      return

    if (destination.droppableId === source.droppableId && destination.index === source.index)
      return

    this.setState(prevState => {
      const movedQuestion = prevState.questions[source.index]
      const newQuestions = prevState.questions

      newQuestions.splice(source.index, 1)
      newQuestions.splice(destination.index, 0, movedQuestion)
      newQuestions.forEach((question, index) => {
        question.position = index
      })

      return { questions: newQuestions }
    }, this.props.onNestedChange)
  }

  duplicateData = async() => {
    const duplicatedData = JSON.parse(JSON.stringify(this.state))

    duplicatedData.id = idGenerator()
    duplicatedData.name = `(Copie) ${duplicatedData.name}`
    duplicatedData.questions = await Promise.all(
      this.state.questions.map(question => SectionField.duplicateQuestion(question, { prefixTitle: false }))
    )
    duplicatedData.removed_question_ids = []
    duplicatedData.errors = {}
    return duplicatedData
  }

  questionsCollapseId = () => `questions_collapse__${this.state.id}`

  contentsCollapseId = () => `contents_collapse__${this.state.id}`

  handleAddQuestionClick = () =>
    SectionField.toggleAddPanel(this.questionsCollapseId(), this.contentsCollapseId())

  handleAddContentClick = () =>
    SectionField.toggleAddPanel(this.contentsCollapseId(), this.questionsCollapseId())

  handleOpenConditions = () => this.props.onOpenConditions(this.state.id)

  addConditionFor = questionId => () => this.props.onAddQuestionCondition(questionId, this.state.id)

  handleClickNewContent = context => event => {
    event.preventDefault()
    SectionField.collapseList(this.contentsCollapseId())

    this.setState(prevState => {
      const newContent = {
        id: idGenerator(),
        title: '',
        description: null,
        context,
        position: prevState.questions.length,
        mandatory: false,
        variable_id: null,
        not_applicable: false,
        picture: null,
        pdf: null,
        isDynamic: false,
        changed: true,
        options: []
      }

      return { questions: prevState.questions.concat(newContent) }
    })
  }

  handleClickNewquestion = context => event => {
    event.preventDefault()
    SectionField.collapseList(this.questionsCollapseId())

    this.setState(prevState => {
      const newQuestion = {
        id: idGenerator(),
        title: '',
        context,
        position: prevState.questions.length,
        mandatory: false,
        variable_id: null,
        not_applicable: false,
        options: []
      }

      return { questions: prevState.questions.concat(newQuestion) }
    })
  }

  addQuestionButton = (context, label) => (
    <button
      type="button"
      className="rounded btn btn-primary mb-1 add-question mr-1"
      onClick={this.handleClickNewquestion(context)}
    >
      {label}
    </button>
  )

  addContentButton = (context, label) => (
    <button
      type="button"
      className="rounded btn btn-primary mb-1 add-content mr-1"
      onClick={this.handleClickNewContent(context)}
    >
      {label}
    </button>
  )

  handleQuestionDuplicate = questionId => async() => {
    const originalQuestion = this.state.questions.find(question => question.id === questionId)
    if (!originalQuestion)
      return

    const questionToDuplicate = await SectionField.duplicateQuestion(originalQuestion)
    this.setState(prevState => {
      const updatedQuestions = [...prevState.questions]
      updatedQuestions.splice(questionToDuplicate.position + 1, 0, questionToDuplicate)
      updatedQuestions.forEach((question, index) => {
        question.position = index
      })
      return { questions: updatedQuestions }
    })
  }

  handleQuestionChange = index => question => {
    this.setState(prevState => {
      const { questions } = prevState

      questions[index] = question

      return { questions }
    }, this.props.onNestedChange)
  }

  removeQuestion = questionId => {
    this.setState(prevState => {
      let newlyRemovedQuestions = [...prevState.removed_question_ids]
      const questions = prevState.questions.filter(
        question => question.id !== questionId
      )

      if (!isNewRecord(questionId))
        newlyRemovedQuestions = prevState.removed_question_ids.concat(questionId)

      questions.forEach((question, index) => {
        question.position = index
      })

      return { questions, removed_question_ids: newlyRemovedQuestions }
    })
    this.props.onQuestionsRemoved([questionId])
  }

  handleQuestionRemove = questionId => () => {
    if (isNewRecord(questionId)) {
      this.removeQuestion(questionId)
      return
    }
    const deleteMessage = 'Tous les résultats associés à cette question seront perdus.'
    dispatchConfirmDelete('Supprimer la question', deleteMessage, () => this.removeQuestion(questionId))
  }

  getErrors = index => () => this.state.errors[`questions[${index}]`]

  conditionalError = index => (this.state.errors[`questions[${index}]`] || {}).conditional || null

  draggableQuestions = () => {
    const questionComponents = {
      grade: Grade,
      photos: Photos,
      selection: Selection,
      selection_multi: Selection,
      comment: Comment,
      numeric: Numeric,
      signature: Signature,
      variable: Variable,
      datetime: DateTime,
      title: Title,
      picture: Picture,
      description: Description,
      pdf: Pdf,
      content_variable: ContentVariable
    }

    return (
      this.state.questions.map((question, index) => {
        const isSelection = SELECTION_CONTEXTS.includes(question.context)
        const canAddCondition = this.props.hasConditionals && SOURCE_CONTEXTS.includes(question.context)
        const handleAddCondition = this.addConditionFor(question.id)
        return (
          <Draggable
            key={`draggable-question-${index}`}
            draggableId={`draggable-question-${index}`}
            index={index}
          >
            {provided => (
              <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                <QuestionConditionsBanner
                  numbers={linkedRuleNumbers(this.props.ruleNumberIndex, this.props.rules, question.id)}
                  error={this.conditionalError(question.id)}
                  onClick={this.handleOpenConditions}
                />
                {Boolean(questionComponents[question.context]) &&
                  createElement(questionComponents[question.context], {
                    key: question.id,
                    name: `${this.fieldName}.questions[${index}]`,
                    question,
                    data: this.props.data,
                    hasConditionals: this.props.hasConditionals,
                    handleQuestionChange: this.handleQuestionChange(index),
                    handleQuestionRemove: this.handleQuestionRemove,
                    handleQuestionDuplicate: this.handleQuestionDuplicate,
                    getErrors: this.getErrors(question.id),
                    dragHandleProps: provided.dragHandleProps,
                    onAddCondition: handleAddCondition
                  })}
                {canAddCondition && !isSelection ? (
                  <div className="tw:mt-2">
                    <AddConditionButton onClick={handleAddCondition} />
                  </div>
                ) : null}
                <hr />
              </div>
            )}
          </Draggable>
        )
      })
    )
  }

  render() {
    return (
      <div className={SECTION_CARD} >
        <div className={SECTION_HEADER_WRAP}>
          <div className={SECTION_HEADER}>
            <i className={DRAG_HANDLE} {...this.props.dragHandleProps} />
            <div className={SECTION_NAME}>
              <span className={SECTION_TITLE}>{this.state.name}</span>
              {this.state.questions.length > 0 ? (
                <Chip variant="neutral">
                  {pluralize(this.state.questions.length, 'question')}
                </Chip>
              ) : null}
              {this.props.hasConditionals && this.props.conditionsCount > 0 ? (
                <Chip variant="info">
                  {pluralize(this.props.conditionsCount, 'condition')}
                </Chip>
              ) : null}
            </div>
            <DuplicateIcon
              className={DUPLICATE_ICON}
              title="Dupliquer"
              onClick={this.props.handleDuplicateSection(this.duplicateData)}
            />
            <TrashCanIcon className={TRASH_ICON} title="Supprimer" onClick={this.handleTrashClick} />
          </div>
        </div>
        <div className={SECTION_BODY}>
          <div className="row">
            <div className="col-md-8">
              <TextField
                name={`${this.fieldName}.name`}
                label="Nom de la section"
                value={this.state.name}
                errors={this.state.errors.name}
                onChange={this.handleNameChange}
              />
            </div>
            <div className="col-md-4">
              <NumberField
                type="number"
                name={`${this.fieldName}.coefficient`}
                label="Coefficient de la section"
                step={1}
                value={this.state.coefficient}
                errors={this.state.errors.coefficient}
                onChange={this.handleCoefficientChange}
              />
            </div>
          </div>
          {
            this.collectFrequency === 'recurrent' && (
              <div className="custom-control custom-switch">
                <input
                  id={`has-disabled-status-${this.state.id}`}
                  type="checkbox"
                  className="custom-control-input custom-switch-lg"
                  checked={this.state.has_disabled_status}
                  onChange={this.handleDisabledStatusChange}
                />
                <label className="custom-control-label" htmlFor={`has-disabled-status-${this.state.id}`}>
                  Avec statut non applicable
                </label>
              </div>
            )
          }
          <hr />
          <div className="question-list">
            <DragDropContext onDragEnd={this.handleOnDragEnd}>
              <Droppable droppableId="droppable-questions">
                {provided => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {this.draggableQuestions()}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
          <div className="tw:flex tw:gap-3 tw:mb-2">
            <Button
              variant="neutral"
              iconNode={<PlusIcon className="tw:w-4 tw:h-4" />}
              onClick={this.handleAddQuestionClick}
            >
              Ajouter une question
            </Button>
            <Button
              variant="neutral"
              iconNode={<PlusIcon className="tw:w-4 tw:h-4" />}
              onClick={this.handleAddContentClick}
            >
              Ajouter du contenu
            </Button>
          </div>
          <div className="row">
            <div className="col">
              <div className="collapse multi-collapse" id={this.questionsCollapseId()}>
                {this.addQuestionButton('grade', 'Notation')}
                {this.addQuestionButton('photos', 'Photos')}
                {this.addQuestionButton('selection', 'Choix unique')}
                {this.addQuestionButton('selection_multi', 'Choix multiples')}
                {this.addQuestionButton('comment', 'Commentaire')}
                {this.addQuestionButton('numeric', 'Numérique')}
                {this.addQuestionButton('signature', 'Signature')}
                {this.addQuestionButton('variable', 'Variable')}
                {this.addQuestionButton('datetime', 'Date / Heure')}
              </div>
              <div className="collapse multi-collapse" id={this.contentsCollapseId()}>
                {this.addContentButton('title', 'Titre')}
                {this.addContentButton('picture', 'Image')}
                {this.addContentButton('pdf', 'PDF')}
                {this.addContentButton('description', 'Description')}
                {this.addContentButton('content_variable', 'Variable')}
              </div>
            </div>
          </div>
        </div>
      </div >
    )
  }
}

export default SectionField
export { updateErrors }
